import { useEffect, useState } from "react";
import parse from "html-react-parser";
import { haveQuestionsContactText } from "@utils/siteText";
import PaymentOptionsTrigger from "@components/modals/payment-options/PaymentOptionsTrigger";
import TextModal from "@components/modals/TextModal";
import ProductConfiguration from "../product-configuration";
import { ProductProps } from "../Product";
import * as Styled from "../Product.styled";

import { ReactComponent as ExpandSVG } from "@images/expand.svg";
import { ReactComponent as GiftSVG } from "@images/gift.svg";
import { Shopify } from "@models/shopify.model";
import { CheckoutInfoProps } from "../accessory-product";
import { getVariantNodes } from "@utils/getVariantNodes";
import { formatPrice, productDisplayPrice, stripPriceFormat, } from "@utils/pricing";
import { firstAvailableVariant } from "@utils/firstAvailableVariant";
import { SanitySyncVariant } from "@models/product.model";
import { events } from "@providers/analytics/analytics";
import { useUserProfile } from "@providers/profile";
import { useShopifyCart } from "@providers/cart";
import PortableText from "@components/portable-text";
import AffirmCallout from "@components/affirm-callout";
import affirm from "@services/affirm";

export interface BundleProductProps extends ProductProps {
  storefrontProduct: Shopify.Product;
}

const BundleProduct = ({ productDocument, storefrontProduct, modals }: BundleProductProps) => {
  const {
    profileState: { authState },
  } = useUserProfile();
  const { checkout } = useShopifyCart();
  const {
    title,
    subtitle,
    descriptionHtml,
    variants: documentVariants,
    priceRange,
    bundle_settings,
  } = productDocument;
  const { contents } = bundle_settings || {};
  const { promotion_text } = contents || {};

  const [isGuaranteeModalOpen, setIsGuaranteeModalOpen] = useState<boolean>(false);
  const [isMembershipModalOpen, setIsMembershipModalOpen] = useState<boolean>(false);
  const [variantId, setVariantId] = useState<Shopify.ProductVariantId | "">("");
  const [variants, setVariants] = useState<Shopify.ProductVariant[] | SanitySyncVariant[]>(
    documentVariants
  );

  const addToCartCopy = "Add To Cart";
  /**
   * A null defaultVariant means no variants are available for sale.
   */
  const defaultVariantId = firstAvailableVariant(storefrontProduct)?.id;
  /**
   * Populate a default variant_id if the storefrontProduct has an
   * available variant. If it doesn't, only display the priceRange
   * of the product and disable the Add To Cart button.
   */
  const defaultCheckout = {
    variant_id: "",
    price: productDisplayPrice(priceRange)
  };
  const [checkoutInfo, setCheckoutInfo] = useState<CheckoutInfoProps>(defaultCheckout);
  /**
   * Even if a product has no options, it still has a single default variant.
   * Check for more than 1 variant before rendering the variant select.
   */
  const hasMultipleVariants = variants.length > 1;

  const toggleGuaranteeModal = () => setIsGuaranteeModalOpen(!isGuaranteeModalOpen);
  const toggleMembershipModal = () => setIsMembershipModalOpen(!isMembershipModalOpen);

  const onVariantDerived = (variantId: Shopify.ProductVariantId | "") => {
    setVariantId(variantId);
  };

  useEffect(() => {
    if (!affirm) return;
      affirm.refreshUI();
 
  }, [checkoutInfo])

  useEffect(() => {
    if (!storefrontProduct || checkoutInfo.variant_id === variantId) return;
    const activeVariant = getVariantNodes(storefrontProduct).find(
      (_variant) => _variant.id === variantId
    );

    events.product.viewed(authState.value, checkout, activeVariant);

    if (activeVariant) {
      setCheckoutInfo({
        variant_id: activeVariant.id,
        price: formatPrice(Number(activeVariant.price.amount)),
      });
    } else {
      const { minVariantPrice, maxVariantPrice } = storefrontProduct?.priceRange;

      const price = minVariantPrice
        ? productDisplayPrice({
            minVariantPrice: Number(minVariantPrice.amount),
            maxVariantPrice: Number(maxVariantPrice.amount),
          })
        : defaultCheckout.price;

      setCheckoutInfo({
        ...defaultCheckout,
        price,
      });
    }
  }, [variantId]);

  useEffect(() => {
    /**
     * Set the variant without user input when there is only
     * a default variant on the product.
     */
    const needsVariantAssigned = !hasMultipleVariants && storefrontProduct && !variantId;
    if (needsVariantAssigned) setVariantId(defaultVariantId || "");
    /**
     * Upgrade to run-time pricing.
     * Set the variants as the storefrontProduct variants if available
     */
    if (storefrontProduct) {
      const { minVariantPrice, maxVariantPrice } = storefrontProduct.priceRange;
      setCheckoutInfo({
        variant_id: "",
        price: productDisplayPrice({
          minVariantPrice: Number(minVariantPrice.amount),
          maxVariantPrice: Number(maxVariantPrice.amount),
        }),
      });
      setVariants(getVariantNodes(storefrontProduct));
    }
  }, [storefrontProduct]);

  return (
    <>
      <Styled.Details>
        <Styled.Heading>
          <h1 className="h3">{title}</h1>
          {subtitle?.value && <p>{subtitle.value}</p>}
          {descriptionHtml && parse(descriptionHtml)}
        </Styled.Heading>

        <Styled.Meta>
          {contents?.section_heading && <span>{contents.section_heading}</span>}
          {contents?.description && <PortableText value={contents.description} />}
        </Styled.Meta>

        {modals?.membership && (
          <Styled.ModalTrigger>
            <button onClick={toggleMembershipModal}>{modals.membership.trigger_text}</button>
            <TextModal
              toggleModal={toggleMembershipModal}
              mounted={isMembershipModalOpen}
              cmsNode={{
                title: modals.membership.title,
                portableText: modals.membership.copy,
                smallText: haveQuestionsContactText(),
              }}
            />
            <ExpandSVG />
          </Styled.ModalTrigger>
        )}

        {modals?.finance && (
          <Styled.ModalTrigger>
            <PaymentOptionsTrigger
              product={productDocument}
              modals={modals}
              customCopy={modals.finance.trigger_text ?? "Finance Options"}
              variantId={variantId}
            />
            <ExpandSVG />
          </Styled.ModalTrigger>
        )}

        <Styled.Footer>
          <ProductConfiguration
            productDocument={productDocument}
            storefrontProduct={storefrontProduct}
            variantId={variantId}
            onVariantDerived={(variant) => onVariantDerived(variant)}
            addToCartText={
              <>
                {checkoutInfo.price}
                <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                {addToCartCopy}
              </>
            }
          />

          <Styled.AffirmCallout>
            <AffirmCallout
              className="affirm-as-low-as"
              pageType="product"
              total={stripPriceFormat(checkoutInfo.price)}
              learnmore={true}
              />
          </Styled.AffirmCallout>
          {promotion_text?.code && (
            <Styled.BundlePromotion>
              <GiftSVG />
              <p>
                {promotion_text.text} <span>{promotion_text.code}</span>
              </p>
            </Styled.BundlePromotion>
          )}

          <Styled.FreeTrialWrapper>
            {modals?.guarantee && (
              <Styled.FreeTrial>
                <PortableText value={modals.guarantee.trigger_copy} />
                <button onClick={toggleGuaranteeModal}>{modals.guarantee.trigger_text}</button>
                <TextModal
                  toggleModal={toggleGuaranteeModal}
                  mounted={isGuaranteeModalOpen}
                  cmsNode={{
                    title: modals.guarantee.title,
                    portableText: modals.guarantee.copy,
                    smallText: haveQuestionsContactText(),
                  }}
                />
              </Styled.FreeTrial>
            )}
          </Styled.FreeTrialWrapper>
        </Styled.Footer>
      </Styled.Details>
    </>
  );
};

export default BundleProduct;
